import React, { createContext, useContext, useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_COUNTRIES_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";
import { NETHERLANDS_COUNTRY_CODE } from "../../../constants/PricingConstants";
import { DELIVERY_LABELS } from "../../../constants/OrderConstant";
import { message } from "antd";
import i18n from "../../../translations/i18n";

const UseCountries = createContext();

export const CountriesProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const colRef = collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_COUNTRIES_TABLE);

    const unsubscribe = onSnapshot(
      colRef,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({ countryCode: doc.id, ...doc.data() }));
        setCountries(data);
        setError(null);
        setIsLoading(false);
      },
      (err) => {
        console.error("Error listening to countries:", err);
        setError(err.message);
        setIsLoading(false);
        message.error(i18n.t("notifications.failed_to_fetch_countries_data"));
      },
    );

    return () => unsubscribe();
  }, []);

  const getCountryPricing = (countryCode) => {
    const country = countries.find((c) => c.countryCode === (countryCode || NETHERLANDS_COUNTRY_CODE));
    return country?.pricing || null;
  };

  const getCountryDelivery = (countryCode) => {
    const country = countries.find((c) => c.countryCode === (countryCode || NETHERLANDS_COUNTRY_CODE));
    return country?.delivery || null;
  };

  const getCountryDeliveryLabels = (countryCode) => {
    const delivery = getCountryDelivery(countryCode);
    if (!delivery?.deliveryLabels?.length) return DELIVERY_LABELS;

    // Return active delivery labels from database with their full data including deliveryTimeSlots
    const activeLabels = delivery.deliveryLabels
      .filter((dl) => dl.isActive)
      .map((dl) => {
        // Find matching label from constants to get the label text
        const constantLabel = DELIVERY_LABELS.find((cl) => cl.code === dl.deliveryLabel);
        return {
          code: dl.deliveryLabel,
          label: constantLabel?.label || dl.deliveryLabel,
          deliveryTimeSlots: dl.deliveryTimeSlots || [],
          leadDays: dl.leadDays,
        };
      });

    return activeLabels.length > 0 ? activeLabels : DELIVERY_LABELS;
  };

  const getDeliveryLabelLeadDays = (countryCode, deliveryLabel) => {
    const delivery = getCountryDelivery(countryCode);
    const labelConfig = delivery?.deliveryLabels?.find((dl) => dl.deliveryLabel === deliveryLabel);
    return labelConfig?.leadDays ?? 1;
  };

  const getCountriesMap = () => {
    return countries.reduce((map, c) => {
      map[c.countryCode] = c;
      return map;
    }, {});
  };

  return (
    <UseCountries.Provider
      value={{
        isLoading,
        countries,
        error,
        getCountryPricing,
        getCountryDelivery,
        getCountryDeliveryLabels,
        getDeliveryLabelLeadDays,
        getCountriesMap,
      }}
    >
      {children}
    </UseCountries.Provider>
  );
};

export const useCountries = () => {
  const context = useContext(UseCountries);

  if (context === undefined) {
    throw new Error("useCountries must be used within a CountriesProvider");
  }

  return context;
};
